import { useCallback, useSyncExternalStore } from 'react';
import { ApiError, getListingPreview } from '../lib/api';
import { track } from '../lib/analytics';

/**
 * Shared preview player — ONE HTMLAudioElement app-wide (BeatStars behavior):
 * starting a preview anywhere stops whatever was playing. State lives in a
 * module-level store consumed via useSyncExternalStore, so a card in the
 * marketplace grid and the detail screen stay in sync for free.
 *
 * Playback source is the anonymous preview ticket from
 * GET /v1/listings/:id/preview (presigned URL, ~5 min expiry). A 404
 * ("no_preview") marks the listing `unavailable` so the UI can disable its
 * player. If a presigned URL goes stale mid-session (media error after
 * expiry), we re-fetch a fresh ticket once and resume from the same position.
 */

export type PreviewPlayerState = 'idle' | 'loading' | 'playing' | 'paused' | 'unavailable';

interface PlayerSnapshot {
  /** Listing whose preview currently owns the shared audio element. */
  currentId: string | null;
  /** Status of the current listing's playback (idle when nothing is loaded). */
  status: 'idle' | 'loading' | 'playing' | 'paused';
  /** Listings known to have no preview media yet (404 no_preview). */
  unavailable: ReadonlySet<string>;
}

let snapshot: PlayerSnapshot = { currentId: null, status: 'idle', unavailable: new Set() };
const listeners = new Set<() => void>();

let audio: HTMLAudioElement | null = null;
/** Monotonic guard so a slow ticket fetch can't clobber a newer toggle. */
let playSeq = 0;
/** Single stale-URL recovery attempt per listing (avoids error→refetch loops). */
let recoveredFor: string | null = null;

function emit(patch: Partial<PlayerSnapshot>): void {
  snapshot = { ...snapshot, ...patch };
  for (const l of listeners) l();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function getSnapshot(): PlayerSnapshot {
  return snapshot;
}

function getAudio(): HTMLAudioElement {
  if (!audio) {
    audio = new Audio();
    audio.preload = 'none';
    audio.addEventListener('ended', () => emit({ status: 'idle' }));
    audio.addEventListener('error', () => {
      void recoverFromStaleUrl();
    });
  }
  return audio;
}

/** Presigned URLs expire (~5 min): on a media error, mint a fresh ticket once and resume. */
async function recoverFromStaleUrl(): Promise<void> {
  const el = audio;
  const id = snapshot.currentId;
  if (!el || !id || snapshot.status === 'idle') return;
  if (recoveredFor === id) {
    // Second failure for the same listing — give up quietly.
    emit({ currentId: null, status: 'idle' });
    return;
  }
  recoveredFor = id;
  const resumeAt = el.currentTime;
  const wasAudible = snapshot.status === 'playing' || snapshot.status === 'loading';
  try {
    const ticket = await getListingPreview(id);
    if (snapshot.currentId !== id) return; // user moved on meanwhile
    el.src = ticket.url;
    if (resumeAt > 0) {
      el.addEventListener('loadedmetadata', () => {
        el.currentTime = resumeAt;
      }, { once: true });
    }
    if (wasAudible) {
      await el.play();
      emit({ status: 'playing' });
    }
  } catch (err) {
    markMaybeUnavailable(id, err);
  }
}

function markMaybeUnavailable(id: string, err?: unknown): void {
  if (err instanceof ApiError && err.status === 404) {
    const unavailable = new Set(snapshot.unavailable);
    unavailable.add(id);
    emit({ currentId: null, status: 'idle', unavailable });
  } else {
    emit({ currentId: null, status: 'idle' });
  }
}

/** Play/pause the preview for a listing on the shared element. */
async function togglePreview(id: string): Promise<void> {
  if (snapshot.unavailable.has(id)) return;
  const el = getAudio();

  if (snapshot.currentId === id) {
    if (snapshot.status === 'loading') return; // ticket fetch already in flight
    if (snapshot.status === 'playing') {
      el.pause();
      emit({ status: 'paused' });
      return;
    }
    if (snapshot.status === 'paused') {
      try {
        await el.play();
        emit({ status: 'playing' });
      } catch {
        // NotSupported/stale source: the element's error listener re-fetches a fresh URL.
      }
      return;
    }
    // status === 'idle' (e.g. the preview ended) → fall through and start fresh.
  }

  // Starting a new preview stops whatever was playing.
  el.pause();
  recoveredFor = null;
  const seq = ++playSeq;
  emit({ currentId: id, status: 'loading' });

  let url: string;
  try {
    url = (await getListingPreview(id)).url;
  } catch (err) {
    if (seq !== playSeq) return; // superseded by a newer toggle
    markMaybeUnavailable(id, err);
    return;
  }
  if (seq !== playSeq) return;

  el.src = url;
  try {
    await el.play();
    if (seq === playSeq) {
      emit({ status: 'playing' });
      track('preview_played', { listing_id: id });
    }
  } catch {
    if (seq === playSeq) emit({ currentId: null, status: 'idle' });
  }
}

/**
 * Per-listing view over the shared player.
 * state: idle | loading | playing | paused | unavailable — toggle() is play/pause.
 */
export function usePreviewPlayer(listingId: string): { state: PreviewPlayerState; toggle: () => void } {
  const snap = useSyncExternalStore(subscribe, getSnapshot);
  const state: PreviewPlayerState = snap.unavailable.has(listingId)
    ? 'unavailable'
    : snap.currentId === listingId
      ? snap.status
      : 'idle';
  const toggle = useCallback(() => {
    void togglePreview(listingId);
  }, [listingId]);
  return { state, toggle };
}

/** Test-only: tear down the singleton element and store between specs. */
export function resetPreviewPlayerForTests(): void {
  audio?.pause();
  audio = null;
  playSeq += 1;
  recoveredFor = null;
  snapshot = { currentId: null, status: 'idle', unavailable: new Set() };
  for (const l of listeners) l();
}
